import React, { useEffect, useState } from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { SafeAreaView, ScrollView } from 'react-native'
import { CustomHeader } from '../../components/CustomHeader'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { atlTheme } from '../../atlTheme'
import {
  ATLDivider,
  ATLSpacing,
  ATLTextBody,
  ATLTextHead,
  SpacingSizesEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { DialogContent, EventTypes, KeysDeliveryIds } from './constants'
import { openWhatsApp } from '../../utils/openWhatsApp'
import { BRIO_SAC_WHATSAPP_NUMBER } from '../../constants/whatsAppInfo'
import { Raffles } from './Raffles'
import { usePromise } from '../../utils/hooks/usePromise'
import { getUserKeysDeliveryEventInvitations } from '../../services/main/getUserKeysDeliveryEventInvitations'
import { useGlobal } from '../../global/useGlobal'
import { confirmKeysDeliveryInvitation } from '../../services/main/confirmKeysDeliveryInvitation'
import { Loading } from '../../components/loading/Loading'
import { getAvailableVisitationRaffles } from '../../services/main/getAvailableVisitationRaffles'
import { VisitEventStatusType, VisitorStatusType } from '../../constants/types'
import { AppointmentsContainer, Container } from './styles'
import { AppointmentsList } from './AppointmentsList'
import { confirmVisitationToConstruction } from '../../services/main/confirmVisitationToConstruction'
import { declineVisitationToConstruction } from '../../services/main/declineVisitationToConstruction'
import { AppointmentsConfirmation } from './AppointmentsConfirmation'
import { showErrorSnackbar } from '../../components/snackbar/Snackbar'

export const AppointmentsScreen: NavigationStackScreenComponent = () => {
  const [appointmentsData, setAppointmentsData] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [dialogContent, setDialogContent] = useState<DialogContent>()
  const [whatsAppMessage, setWhatsAppMessage] = useState<string>('')
  const [userKeysDeliveryEvent] = useGlobal('userKeysDeliveryEventInvitations')
  const [availableVisitationEvents] = useGlobal('availableVisitationEvents')
  const [shouldRefreshRaffles] = useGlobal('shouldRefreshRaffles')
  const [shouldRefreshKeysDelivery] = useGlobal('shouldRefreshKeysDelivery')
  const [currentVisitationEventId, setCurrentVisitationEventId] = useState<string>()
  const [currentKeysDeliveryEventIds, setCurrentKeysDeliveryEventIds] = useState<KeysDeliveryIds>()

  const { request: fetchKeysDeliveryEvents, loading: isFetchingKeysDeliveryEvent } = usePromise(
    getUserKeysDeliveryEventInvitations
  )
  const { request: fetchRaffles, loading: isFetchingVisitationEventsAndRaffles } = usePromise(
    getAvailableVisitationRaffles
  )
  const { request: confirmPresenceOnVisitation, loading: isConfirmingPresenceOnVisitation } = usePromise(
    confirmVisitationToConstruction
  )
  const { request: declinePresenceOnVisitation, loading: isDecliningPresenceOnVisitation } = usePromise(
    declineVisitationToConstruction
  )
  const { request: declarePresenceOnKeysDelivery, loading: isDeclaringPresenceOnKeysDelivery } = usePromise(
    confirmKeysDeliveryInvitation,
    [],
    {
      onSuccess: () => {
        fetchKeysDeliveryEvents()
      },
    }
  )

  //only visitation events pending raffle to which the user can enroll
  const availableRaffles = availableVisitationEvents?.filter(
    event => event.visitEvent.eventStatus === VisitEventStatusType.open
  )
  //only events already raffled with defined potential attendees (they can confirm or decline)
  const availableEvents = availableVisitationEvents?.filter(
    event =>
      event?.visitEvent?.eventStatus === VisitEventStatusType.sorted &&
      (event?.subscription?.status === VisitorStatusType.sorted ||
        event?.subscription?.status === VisitorStatusType.confirmed)
  )

  const isShowingEmptyState =
    appointmentsData?.every(
      item =>
        item?.keysDeliveryEvent?.isCanceled === true ||
        item?.keysDeliveryEvent?.isPublished === false ||
        item?.isAttending === false
    ) && !availableEvents?.length

  //load data on first render
  useEffect(() => {
    fetchKeysDeliveryEvents()
    fetchRaffles()
  }, [])

  //refresh data whenever a change happens, but without duplication of that type of data
  useEffect(() => {
    if (shouldRefreshRaffles) {
      fetchRaffles()
    }
  }, [shouldRefreshRaffles])

  useEffect(() => {
    if (shouldRefreshKeysDelivery) {
      fetchKeysDeliveryEvents()
    }
  }, [shouldRefreshKeysDelivery])

  const populateScheduleEvents = async () => {
    const list = []
    await userKeysDeliveryEvent?.forEach(event => list.push(event))
    await availableEvents?.forEach(event => list.push(event))
    await setAppointmentsData(list)
  }

  useEffect(() => {
    populateScheduleEvents()
  }, [availableVisitationEvents, userKeysDeliveryEvent])

  const onCancelKeysDelivery = async () => {
    dismissDialog()

    if (currentKeysDeliveryEventIds) {
      await declarePresenceOnKeysDelivery({
        invitationId: currentKeysDeliveryEventIds.invitationId,
        eventId: currentKeysDeliveryEventIds.eventId,
        isAttending: false,
      })
    } else {
      showErrorSnackbar(formatMessage(messages.appointments_couldNotDeclinePresence))
    }
  }

  const declineVisitationInvite = () => {
    dismissDialog()
    if (currentVisitationEventId) {
      declinePresenceOnVisitation(currentVisitationEventId)
    } else {
      showErrorSnackbar(formatMessage(messages.appointments_couldNotDeclinePresence))
    }
  }

  const dismissDialog = () => {
    setIsDialogOpen(false)
  }

  const onPressContactUs = () => {
    dismissDialog()
    openWhatsApp(whatsAppMessage, BRIO_SAC_WHATSAPP_NUMBER)
  }

  const onConfirmKeysDelivery = (invitationId: string, eventId: string) => {
    declarePresenceOnKeysDelivery({
      invitationId,
      eventId,
      isAttending: true,
    })
  }

  const onConfirmVisitationToConstruction = (id: string) => {
    confirmPresenceOnVisitation(id)
  }

  const cancelPresenceDialog: { [key: string]: DialogContent } = {
    [EventTypes.KEYS_DELIVERY]: {
      title: formatMessage(messages.appontments_cancelLabel),
      body: formatMessage(messages.appointments_keysDeliveryCancelDialog),
      buttonTexts: [
        formatMessage(messages.appointments_dialogButtonConfirm),
        formatMessage(messages.appointments_dialogButtonDismiss),
      ],
      onPressConfirm: onPressContactUs,
      onPressDismiss: onCancelKeysDelivery,
    },
    [EventTypes.VISIT_TO_CONSTRUCTION]: {
      title: formatMessage(messages.appontments_cancelLabel),
      body: formatMessage(messages.appointments_visitationToConstructionCancelDialog),
      buttonTexts: [
        formatMessage(messages.appointments_dialogButtonConfirm),
        formatMessage(messages.appointments_dialogButtonDismiss),
      ],
      onPressConfirm: declineVisitationInvite,
      onPressDismiss: dismissDialog,
    },
  }

  const onPressCancelKeysDelivery = async (invitationId: string, eventId: string, constructionName: string) => {
    await setCurrentKeysDeliveryEventIds({ invitationId, eventId })
    setDialogContent(cancelPresenceDialog[EventTypes.KEYS_DELIVERY])
    setIsDialogOpen(true)
    setWhatsAppMessage(`${formatMessage(messages.appointments_keysDeliveryWhatsAppMessage, { constructionName })}`)
  }
  const onPressCancelVisitationToConstruction = (id: string) => {
    setCurrentVisitationEventId(id)
    setDialogContent(cancelPresenceDialog[EventTypes.VISIT_TO_CONSTRUCTION])
    setIsDialogOpen(true)
  }

  const isLoading = isFetchingKeysDeliveryEvent || isFetchingVisitationEventsAndRaffles

  return isLoading ? (
    <>
      <ATLSpacing type={SpacingSizesEnum.marginal} />
      <Loading />
    </>
  ) : (
    <SafeAreaView>
      <ScrollView>
        <AppointmentsContainer>
          <ATLTextHead
            textAlignment={TextAlignmentEnum.left}
            labelBaseProps={{ textColor: atlTheme.ColorHighContrast }}
          >
            {formatMessage(messages.appointments_appointmentsTitle)}
          </ATLTextHead>
          <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />
          {isShowingEmptyState ? (
            <ATLTextBody labelBaseProps={{ textColor: atlTheme.ColorMediumContrast, multilineLimit: 2 }}>
              {formatMessage(messages.appointments_noAppointments)}
            </ATLTextBody>
          ) : (
            <AppointmentsList
              isLoading={isLoading}
              appointments={appointmentsData}
              onPressCancelKeysDelivery={onPressCancelKeysDelivery}
              onPressConfirmKeysDelivery={onConfirmKeysDelivery}
              onPressCancelVisitation={onPressCancelVisitationToConstruction}
              onPressConfirmVisitation={onConfirmVisitationToConstruction}
            />
          )}
        </AppointmentsContainer>
        <Container>
          <ATLTextHead
            textAlignment={TextAlignmentEnum.left}
            labelBaseProps={{ textColor: atlTheme.ColorHighContrast }}
          >
            {formatMessage(messages.appointments_rafflesTitle)}
          </ATLTextHead>
          <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />
          <Raffles availableRaffles={availableRaffles} />
        </Container>
        <AppointmentsConfirmation dialogContent={dialogContent} isDialogOpen={isDialogOpen} />
        <ATLSpacing type={SpacingSizesEnum.intersection} />
      </ScrollView>
    </SafeAreaView>
  )
}

AppointmentsScreen.navigationOptions = CustomHeader(
  formatMessage(messages.dashboard_bottomTabs_appointments),
  true,
  () => Coordinator.goBack()
)
